import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import GlassPicker from "./GlassPicker";

export const DEFAULTS = {
  curtain_wall: { width_m: 12, height_m: 10.8, floor_height_m: 3.6, mullion_spacing_m: 1.5, transom_spacing_m: 1.8, frame_material: "aluminium", glass_type: "D-6HS-12-6HS", profile_source: "bhoruka" },
  skylight: { length_m: 8, width_m: 6, slope_deg: 15, form: "ridge", rafter_spacing_m: 1.2, glazing_bar_spacing_m: 1.0, live_load_kpa: 0.75, frame_material: "aluminium", glass_type: "D-6FT-12-6.38HSPVB" },
  canopy: { projection_m: 2.5, width_m: 6, outrigger_spacing_m: 1.5, glazing_bar_spacing_m: 1.0, live_load_kpa: 0.75, frame_material: "steel", glass_type: "L-25.52FTSGP" },
};

export const BWAY_DEFAULTS = { width_m: 3.6, height_m: 6.8, floor_height_m: 3.4, mullion_spacing_m: 1.2, transom_spacing_m: 1.8, frame_material: "aluminium",
  glass_type: "D-6FT-12-6FT", profile_source: "bway", bway_series: "150S", bway_variant: "SA", design_code: "adm", mullion_support: "continuous_2", stack_offset_m: 0.4 };
const SOURCE_OPTS = [["generic", "Generic catalogue"], ["bhoruka", "Bhoruka profiles"], ["bway", "BWAY Slimpro"]];
const FORM_OPTS = [["ridge", "Ridge (dual pitch)"], ["mono", "Mono-pitch"]];
const SERIES_OPTS = [["25100S", "Slim Pro 25100S · 8011 tube"], ["150S", "150S · 150S-L24 (report)"]];
const VARIANT_OPTS = [["SA", "SA · 50 mm outer box"], ["SB", "SB · 19 mm cap"], ["SC", "SC · flush"]];
const FRAME_OPTS = [["aluminium", "Aluminium 6063-T6"], ["steel", "Steel IS 2062 E250"]];

const SUPPORTS = [["simple", "Simple span (1 floor)"], ["continuous_2", "Continuous over 2 floors"], ["continuous_3", "Continuous over 3 floors"]];
const TRANSOMS = [["auto", "Auto (lightest that passes, 8011 first)"], ["bway_8011", "BWAY 8011 · 25 × 100"], ["bway_150S-L24", "BWAY 150S-L24 · 25 × 144"]];
const CODES = [["is_aama", "IS 875-3 + AAMA (fy/1.5)"], ["adm", "ADM 6063-T6 (report method)"]];

const FIELDS = {
  curtain_wall: [["width_m", "Total width", "m"], ["height_m", "Total height", "m"], ["floor_height_m", "Floor-to-floor (mullion span)", "m"], ["mullion_spacing_m", "Mullion spacing", "m"], ["transom_spacing_m", "Transom spacing", "m"]],
  skylight: [["length_m", "Length (along ridge)", "m"], ["width_m", "Width (span)", "m"], ["slope_deg", "Slope", "°"], ["rafter_spacing_m", "Rafter spacing", "m"], ["glazing_bar_spacing_m", "Glazing bar spacing", "m"], ["live_load_kpa", "Live load", "kPa"]],
  canopy: [["projection_m", "Projection (cantilever)", "m"], ["width_m", "Width", "m"], ["outrigger_spacing_m", "Outrigger spacing", "m"], ["glazing_bar_spacing_m", "Glazing bar spacing", "m"], ["live_load_kpa", "Live load", "kPa"]],
};

const Lbl = ({ children }) => <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{children}</span>;

const Pick = ({ label, value, onChange, options, testid }) => (
  <label className="space-y-1">
    <Lbl>{label}</Lbl>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="h-9 border-zinc-800 bg-zinc-900 text-sm"><SelectValue /></SelectTrigger>
      <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  </label>
);

export default function DesignForm({ type, inputs, onChange, project, brand }) {
  const page = brand === "bway";
  const bway = page || inputs.profile_source === "bway";
  const setSource = (v) => {
    if (v === "bway") onChange({ ...inputs, ...BWAY_DEFAULTS, width_m: inputs.width_m, height_m: inputs.height_m, floor_height_m: inputs.floor_height_m, transom_spacing_m: inputs.transom_spacing_m });
    else onChange({ ...inputs, profile_source: v, transom_id: undefined, mullion_id: undefined, glass_type: inputs.profile_source === "bway" ? DEFAULTS.curtain_wall.glass_type : inputs.glass_type });
  };
  const sourcePick = <Pick label="Profile source" testid="input-profile-source" value={inputs.profile_source || "generic"} onChange={setSource} options={SOURCE_OPTS} />;
  const set = (k) => (v) => onChange({ ...inputs, [k]: v });
  const manualWind = inputs.wind_pressure_kpa !== undefined && inputs.wind_pressure_kpa !== null;

  return (
    <div className="space-y-5" data-testid="design-form">
      <div className="grid grid-cols-2 gap-3">
        {FIELDS[type].map(([k, label, unit]) => (
          <label key={k} className="space-y-1">
            <Lbl>{label} ({unit})</Lbl>
            <Input data-testid={`input-${k}`} type="number" step="0.05" min="0" value={inputs[k] ?? ""} onChange={(e) => set(k)(e.target.value === "" ? "" : Number(e.target.value))} className="h-9 border-zinc-800 bg-zinc-900 font-mono text-sm" />
          </label>
        ))}
        {type === "skylight" && <Pick label="Form" testid="input-form" value={inputs.form} onChange={set("form")} options={FORM_OPTS} />}
      </div>
      {bway ? (
        <div className="grid grid-cols-2 gap-3" data-testid="bway-system-fields">
          {!page && sourcePick}
          <Pick label="BWAY series" testid="input-bway-series" value={inputs.bway_series || "25100S"} onChange={set("bway_series")} options={SERIES_OPTS} />
          <Pick label="Outer variant" testid="input-bway-variant" value={inputs.bway_variant || "SA"} onChange={set("bway_variant")} options={VARIANT_OPTS} />
          <Pick label="Design code" testid="input-design-code" value={inputs.design_code || "adm"} onChange={set("design_code")} options={CODES} />
          <Pick label="Transom tube" testid="input-bway-transom" value={inputs.transom_id || "auto"} onChange={(v) => onChange({ ...inputs, transom_id: v === "auto" ? undefined : v })} options={TRANSOMS} />
        </div>
      ) : (
      <div className="grid grid-cols-2 gap-3">
        <Pick label="Frame material" testid="input-frame-material" value={inputs.frame_material} onChange={set("frame_material")} options={FRAME_OPTS} />
        {type === "curtain_wall" && inputs.frame_material === "aluminium" && (
          sourcePick
        )}
        {type === "curtain_wall" && inputs.frame_material === "aluminium" && (
          <Pick label="Design code" testid="input-design-code" value={inputs.design_code || "is_aama"} onChange={set("design_code")} options={CODES} />
        )}
      </div>
      )}
      {type === "curtain_wall" && (
        <div className="grid grid-cols-2 gap-3" data-testid="mullion-support-fields">
          <Pick label="Mullion support" testid="input-mullion-support" value={inputs.mullion_support || "simple"} onChange={set("mullion_support")} options={SUPPORTS} />
          {(inputs.mullion_support || "simple") !== "simple" && (
            <label className="space-y-1">
              <Lbl>Stack joint above bracket (m)</Lbl>
              <Input data-testid="input-stack-offset" type="number" step="0.05" min="0" value={inputs.stack_offset_m ?? 0.4} onChange={(e) => set("stack_offset_m")(e.target.value === "" ? "" : Number(e.target.value))} className="h-9 border-zinc-800 bg-zinc-900 font-mono text-sm" />
            </label>
          )}
        </div>
      )}
      <div><span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Glass make-up{bway ? " (Slim Pro: 18–32 mm)" : ""}</span><GlassPicker value={inputs.glass_type} onChange={set("glass_type")} testid="input-glass" range={bway ? [18, 32.5] : undefined} /></div>
      <div className="rounded-md border border-zinc-800 p-3">
        <div className="mb-2 flex items-center justify-between">
          <Lbl>Wind pressure</Lbl>
          <label className="flex items-center gap-2 text-xs text-zinc-400">
            Manual
            <Switch data-testid="manual-wind-switch" checked={manualWind} onCheckedChange={(c) => {
              const next = { ...inputs };
              if (c) next.wind_pressure_kpa = 1.5; else delete next.wind_pressure_kpa;
              onChange(next);
            }} />
          </label>
        </div>
        {manualWind ? (
          <Input data-testid="input-wind_pressure_kpa" type="number" step="0.05" value={inputs.wind_pressure_kpa} onChange={(e) => set("wind_pressure_kpa")(Number(e.target.value))} className="h-9 border-zinc-800 bg-zinc-900 font-mono text-sm" />
        ) : (
          <p className="text-xs text-zinc-400" data-testid="auto-wind-info">IS 875-3 from project data: Vb {project.basic_wind_speed} m/s, H {project.building_height_m} m, terrain category {project.terrain_category}</p>
        )}
      </div>
    </div>
  );
}
